import React, { useState, useMemo } from 'react'
import useSWR from 'swr'
import { fetcher } from '@/hooks'
import PamAppSelect from './components/PamAppSelect'
import PamMediationGroupsTable from './components/PamMediationGroupsTable'
import PamImportModal from './components/PamImportModal'
import PamEditPanel from './components/PamEditPanel'
import PamWaterfallEditModal from './components/PamWaterfallEditModal'
import PamSubmitConfirmDialog from './components/PamSubmitConfirmDialog'
import PamMappingInfoModal from './components/PamMappingInfoModal'
import Skeleton from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'

/**
 * PAM 自动导入页面
 * 批量导入和管理 AdMob 中介组 waterfall 价格
 */
function PamAutoImportPage() {
  const [selectedAppId, setSelectedAppId] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [selectedGroups, setSelectedGroups] = useState([])
  const [editingGroups, setEditingGroups] = useState([])
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [isWaterfallModalOpen, setIsWaterfallModalOpen] = useState(false)
  const [currentEditingGroup, setCurrentEditingGroup] = useState(null)
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false)
  const [changedGroupsToSubmit, setChangedGroupsToSubmit] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitResult, setSubmitResult] = useState(null)
  const [isMappingInfoModalOpen, setIsMappingInfoModalOpen] = useState(false)
  const [currentInfoGroup, setCurrentInfoGroup] = useState(null)

  // Fetch app list
  const {
    data: apps,
    error: appsError,
    isLoading: isLoadingApps,
  } = useSWR(['/admob/pam/apps'])

  // Fetch mediation groups for selected app
  const {
    data: mediationGroups,
    error: groupsError,
    isLoading: isLoadingGroups,
    mutate: mutateMediationGroups,
  } = useSWR(
    selectedAppId
      ? ['/admob/pam/mediation-groups', { pid: selectedAppId }]
      : null,
  )

  // Filter mediation groups by search term
  const filteredGroups = useMemo(() => {
    if (!mediationGroups) return []
    if (!searchTerm) return mediationGroups

    const lowerSearch = searchTerm.toLowerCase()
    return mediationGroups.filter((group) =>
      group.name.toLowerCase().includes(lowerSearch),
    )
  }, [mediationGroups, searchTerm])

  const handleAppChange = (appId) => {
    setSelectedAppId(appId)
    setSearchTerm('')
    setSelectedGroups([])
    setEditingGroups([])
    setIsEditPanelOpen(false)
  }

  const handleSelectAll = () => {
    // 只选择没有A/B实验的中介组
    const editableGroups = filteredGroups.filter((g) => !g.hasAbTest)
    const editableIds = editableGroups.map((g) => g.id)
    
    if (selectedGroups.length === editableIds.length) {
      setSelectedGroups([])
    } else {
      setSelectedGroups(editableIds)
    }
  }

  const handleSelectGroup = (groupId) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    )
  }

  const handleEdit = (group) => {
    // 不允许编辑有A/B实验的中介组
    if (group.hasAbTest) {
      alert('该中介组正在进行A/B实验，暂时无法编辑')
      return
    }
    
    // Add to editing list if not already there
    if (!editingGroups.some((g) => g.id === group.id)) {
      setEditingGroups((prev) => [...prev, { ...group, draft: null }])
    }
    setIsEditPanelOpen(true)
  }

  const handleBatchEdit = () => {
    // 过滤掉有A/B实验的中介组
    const groupsToEdit = filteredGroups.filter(
      (g) => selectedGroups.includes(g.id) && !g.hasAbTest,
    )
    const newEditingGroups = groupsToEdit.filter(
      (g) => !editingGroups.some((eg) => eg.id === g.id),
    )
    setEditingGroups((prev) => [
      ...prev,
      ...newEditingGroups.map((g) => ({ ...g, draft: null })),
    ])
    setIsEditPanelOpen(true)
  }

  const handleOpenImportModal = () => {
    if (!selectedAppId) {
      alert('请先选择一个应用')
      return
    }
    setIsImportModalOpen(true)
  }

  const handleRefreshGroups = async () => {
    if (!selectedAppId) {
      return
    }
    await mutateMediationGroups()
  }

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false)
  }

  const handleImportConfirm = (parsedGroups) => {
    // Apply imported prices to editing groups
    const updatedGroups = []

    parsedGroups.forEach((parsed) => {
      if (!parsed.exists || parsed.prices.length === 0) return

      const group = mediationGroups.find((g) => g.name === parsed.name)
      if (!group || group.hasAbTest) return

      // Create new waterfall with imported Admob prices
      const nonAdmobItems = group.waterfall.filter(
        (item) => item.name !== 'Admob',
      )
      const newAdmobItems = parsed.prices.map((price, index) => ({
        id: `imported-admob-${Date.now()}-${index}`,
        name: 'Admob',
        price,
      }))

      const newWaterfall = [...nonAdmobItems, ...newAdmobItems].sort(
        (a, b) => b.price - a.price,
      )

      updatedGroups.push({
        ...group,
        draft: newWaterfall,
      })
    })

    // Add to editing list or update existing
    setEditingGroups((prev) => {
      const updated = [...prev]
      updatedGroups.forEach((newGroup) => {
        const existingIndex = updated.findIndex((g) => g.id === newGroup.id)
        if (existingIndex >= 0) {
          updated[existingIndex] = newGroup
        } else {
          updated.push(newGroup)
        }
      })
      return updated
    })

    setIsImportModalOpen(false)
    setIsEditPanelOpen(true)
  }

  const handleCloseEditPanel = () => {
    setIsEditPanelOpen(false)
  }

  const handleEditWaterfall = (group) => {
    setCurrentEditingGroup(group)
    setIsWaterfallModalOpen(true)
  }

  const handleSaveWaterfall = (groupId, newWaterfall) => {
    setEditingGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, draft: newWaterfall } : g)),
    )
    setIsWaterfallModalOpen(false)
    setCurrentEditingGroup(null)
  }

  const handleRemoveGroup = (groupId, resetOnly) => {
    if (resetOnly) {
      // Reset draft changes
      setEditingGroups((prev) =>
        prev.map((g) => (g.id === groupId ? { ...g, draft: null } : g)),
      )
    } else {
      // Remove from editing list
      setEditingGroups((prev) => prev.filter((g) => g.id !== groupId))
    }
  }

  const handleSubmitChanges = () => {
    const changedGroups = editingGroups.filter((g) => g.draft !== null)

    if (changedGroups.length === 0) {
      alert('没有需要提交的更改')
      return
    }

    // Reset submit state and open confirmation dialog
    setSubmitResult(null)
    setIsSubmitting(false)
    setChangedGroupsToSubmit(changedGroups)
    setIsSubmitConfirmOpen(true)
  }

  const handleConfirmSubmit = async () => {
    // Start submitting (don't close dialog)
    setIsSubmitting(true)
    setSubmitResult(null)

    // Prepare update payload
    const payload = {
      groups: changedGroupsToSubmit.map((g) => ({
        groupId: g.id,
        groupResourceName: g.resourceName,
        groupName: g.name,
        groupType: g.type,
        groupAdUnitIds: g.adUnitIds,
        waterfall: g.draft,
      })),
    }

    try {
      const result = await fetcher([
        '/admob/pam/mediation-groups/batch-update',
        { pid: selectedAppId },
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      ])

      const completedIds = result.updatedGroupIds || []
      const allSucceeded = result.updatedCount === changedGroupsToSubmit.length
      const failures = (result.errors || [])
        .map((item) => `${item.groupName}: ${item.error}`)
        .join('; ')
      setIsSubmitting(false)
      setSubmitResult({
        success: allSucceeded,
        message: `Created A/B experiments for ${result.updatedCount} of ${changedGroupsToSubmit.length} mediation groups.${failures ? ` ${failures}` : ''}`,
      })

      // Keep unsuccessful drafts and remove groups whose experiments were created.
      setEditingGroups((prev) => prev.filter((group) =>
        !completedIds.includes(group.id) &&
        !(allSucceeded && changedGroupsToSubmit.some((item) => item.id === group.id)),
      ))
      setSelectedGroups([])
      if (allSucceeded) setIsEditPanelOpen(false)

      // Refresh separately so a read failure cannot turn a completed write into a failure.
      void mutateMediationGroups().catch((error) => {
        console.error('Failed to refresh mediation groups:', error)
      })
    } catch (error) {
      console.error('Submit error:', error)

      // Set error result
      setIsSubmitting(false)
      setSubmitResult({
        success: false,
        message: `提交失败: ${error.message}`,
      })
    }
  }

  const handleCancelSubmit = () => {
    setIsSubmitConfirmOpen(false)
    setChangedGroupsToSubmit([])
    setIsSubmitting(false)
    setSubmitResult(null)
  }

  const handleShowInfo = (group) => {
    setCurrentInfoGroup(group)
    setIsMappingInfoModalOpen(true)
  }

  const handleCloseMappingInfo = () => {
    setIsMappingInfoModalOpen(false)
    setCurrentInfoGroup(null)
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Mediation A/B</h1>
        <p className="text-gray-600">
          Test AdMob waterfall prices for PAM and non-PAM mediation groups.
          Each experiment applies to all ad units linked to the selected group.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {/* Filter Section */}
        <div className="mb-6 space-y-4">
          <h2 className="text-lg font-semibold">筛选条件</h2>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="w-[452px] shrink-0">
              {isLoadingApps ? (
                <Skeleton className="h-10 w-full" />
              ) : appsError ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    加载应用列表失败: {appsError.message}
                  </AlertDescription>
                </Alert>
              ) : (
                <PamAppSelect
                  apps={apps}
                  value={selectedAppId}
                  onChange={handleAppChange}
                  isLoading={isLoadingApps}
                />
              )}
            </div>
            <div className="flex items-center gap-3 justify-end flex-1 min-w-[560px]">
              <div className="w-[360px] shrink-0">
                <Input
                  type="text"
                  placeholder="搜索中介组名称..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={!selectedAppId}
                />
              </div>
              <Button
                onClick={handleOpenImportModal}
                disabled={!selectedAppId}
                variant="default"
              >
                批量导入
              </Button>
              <Button
                onClick={handleRefreshGroups}
                disabled={!selectedAppId || isLoadingGroups}
                variant="outline"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingGroups ? 'animate-spin' : ''}`} />
                刷新
              </Button>
            </div>
          </div>
        </div>

        {/* Batch Actions Bar */}
        {selectedGroups.length > 0 && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center">
            <span className="text-sm font-medium">
              已选择 {selectedGroups.length} 个中介组
            </span>
            <Button onClick={handleBatchEdit} size="sm">
              批量编辑 ({selectedGroups.length})
            </Button>
          </div>
        )}

        {/* Mediation Groups List */}
        {selectedAppId && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-4">
              中介组列表
              {filteredGroups.length > 0 && ` (${filteredGroups.length})`}
            </h2>
            {isLoadingGroups ? (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : groupsError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  加载中介组失败: {groupsError.message}
                </AlertDescription>
              </Alert>
            ) : (
              <PamMediationGroupsTable
                groups={filteredGroups}
                selectedGroups={selectedGroups}
                onSelectAll={handleSelectAll}
                onSelectGroup={handleSelectGroup}
                onEdit={handleEdit}
                onShowInfo={handleShowInfo}
              />
            )}
          </div>
        )}
      </div>

      {/* Import Modal */}
      <PamImportModal
        isOpen={isImportModalOpen}
        onClose={handleCloseImportModal}
        selectedAppId={selectedAppId}
        mediationGroups={mediationGroups}
        onConfirm={handleImportConfirm}
      />

      {/* Edit Panel */}
      <PamEditPanel
        isOpen={isEditPanelOpen}
        editingGroups={editingGroups}
        onClose={handleCloseEditPanel}
        onEditWaterfall={handleEditWaterfall}
        onRemoveGroup={handleRemoveGroup}
        onSubmit={handleSubmitChanges}
      />

      {/* Waterfall Edit Modal */}
      <PamWaterfallEditModal
        isOpen={isWaterfallModalOpen}
        group={currentEditingGroup}
        onClose={() => setIsWaterfallModalOpen(false)}
        onSave={handleSaveWaterfall}
      />

      {/* Submit Confirmation Dialog */}
      <PamSubmitConfirmDialog
        isOpen={isSubmitConfirmOpen}
        changedGroups={changedGroupsToSubmit}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
        isSubmitting={isSubmitting}
        submitResult={submitResult}
      />

      {/* Mapping Info Modal */}
      <PamMappingInfoModal
        isOpen={isMappingInfoModalOpen}
        onClose={handleCloseMappingInfo}
        group={currentInfoGroup}
        pid={selectedAppId}
      />
    </div>
  )
}

export default PamAutoImportPage
